import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import MessageIcon from "@/assets/colorable/message.svg?react";
import StarIcon from "@/assets/colorable/star.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import ActivityIcon from "@/assets/light_gradient/activity.svg?react";
import EngagementIcon from "@/assets/light_gradient/engagement.svg?react";
import UsersIcon from "@/assets/light_gradient/users.svg?react";
import ViewsIcon from "@/assets/light_gradient/views.svg?react";
import Button from "@/components/Common/Button";
import InfluencerSocials from "@/components/Common/Influencer/InfluencerCard/InfluencerSocials";
import TopFollowerCountry from "@/components/Common/Influencer/InfluencerCard/TopFollowerCountry";
import OverlappingAvatars from "@/components/Common/OverlappingAvatars";
import {Tooltip} from "@/components/Common/Tooltip";
import TruncatedTextWithMore from "@/components/Common/TruncatedTextWithMore";
import InfluencerStatisticsRow from "@/components/Influencer/InfluencerDetail/InfluencerStatisticsRow";
import type {AmbassadorshipRequestBrandSerializer, PotentialAmbassadorSerializer} from "@/generated/DjangoTypes";
import {useSocialNumberFormat} from "@/hooks/useNumberFormat";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {FULL_DATE_FORMAT} from "@/store/constants";
import {TSidebarInfluencer} from "@/store/influencerSidebarContext";
import {ambassadorActionModalAtom} from "@/store/jotaiAtoms";
import {TInfluencerStatistics} from "@/store/types";
import {scrollToInfluencerSidebarMessage} from "@/utils/sidebarUtils";
import {Link} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";

const AmbassadorApplication: FC<
  {
    agencyId?: number;
    companyId?: number;
    footer?: ReactNode;
    groupId?: number;
    isSelected?: boolean;
    onSelect?: () => void;
    showActions?: boolean;
  } & (
    | {ambassador: PotentialAmbassadorSerializer; source?: "pending_queue"}
    | {ambassador: AmbassadorshipRequestBrandSerializer; source: "sent_request"}
  )
> = ({agencyId, ambassador, companyId, footer, groupId, isSelected, onSelect, showActions = true, source}) => {
  const {t} = useTranslation(["common", "influencer_profile", "referral"]);
  const formatNumber = useSocialNumberFormat();
  const setAmbassadorActionModalOpen = useSetAtom(ambassadorActionModalAtom);
  const influencer = ambassador.influencer;
  const isSentRequest = source === "sent_request";
  const campaignsTooltipId = `completed-campaigns-of-ambassador-${influencer?.uuid}`;
  const lastRatingTooltipId = `last-rating-of-ambassador-${influencer?.uuid}`;
  const messageTooltipId = `message-of-ambassador-${influencer?.uuid}`;
  const lastRatedCampaign = ambassador.completed_campaigns?.find(campaign => campaign.rating !== null);
  const influencerSearchName =
    [influencer?.user.first_name, influencer?.user.last_name].filter(Boolean).join(" ") ||
    influencer?.user.username ||
    influencer?.user.tiktok_username ||
    undefined;

  const influencerVerificationCategory = influencer?.user.is_verified
    ? "instagram"
    : influencer?.user.tiktok_verified
      ? "tiktok"
      : influencer?.user.ugc_verified
        ? "ugc"
        : null;

  const dateVerified =
    influencerVerificationCategory === "tiktok"
      ? ambassador.date_verified?.tiktok
      : influencerVerificationCategory === "instagram"
        ? ambassador.date_verified?.instagram
        : ambassador.date_verified?.ugc;

  const formattedDateVerified =
    dateVerified && dayjs(dateVerified).isValid() ? dayjs(dateVerified).format(FULL_DATE_FORMAT) : null;

  const getInstagramStats = () => {
    const stats: TInfluencerStatistics[] = [
      ...(influencer?.user.is_verified && influencer?.graphs?.countries.length
        ? [
            {
              component: (
                <TopFollowerCountry
                  className="justify-end"
                  graphs={influencer?.graphs}
                  influencerVerificationCategory="instagram"
                  textClassName="text-sm leading-4"
                />
              ),
            },
          ]
        : []),
      {
        component: <EngagementIcon className="size-3.5 shrink-0" />,
        text: `${influencer?.engagement ?? "?? "}%`,
        tooltipTextTranslation: "engagement_rate",
      },
    ];

    if (influencer?.instagram_median_views) {
      stats.push({
        component: <ViewsIcon className="size-3.5 shrink-0" />,
        text: formatNumber(influencer?.instagram_median_views),
        tooltipTextTranslation: "median_views",
        textParam: t("instagram"),
      });
    }

    stats.push({
      component: <UsersIcon className="size-3.5 shrink-0" />,
      text: formatNumber(influencer?.followers),
      tooltipTextTranslation: "follower_count",
    });

    return stats;
  };

  const getTiktokStats = () => {
    const stats: TInfluencerStatistics[] = [
      ...(influencer?.user.tiktok_verified && influencer?.graphs?.tiktok_countries?.length
        ? [
            {
              component: (
                <TopFollowerCountry
                  className="justify-end"
                  graphs={influencer?.graphs}
                  influencerVerificationCategory="tiktok"
                  textClassName="text-sm"
                />
              ),
            },
          ]
        : []),
      {
        component: <ActivityIcon className="size-3.5 shrink-0" />,
        text: `${influencer?.tiktok_last_period_video_count ?? "?? "}${t("influencer_profile:per_month_abbreviation")}`,
        tooltipTextTranslation: "activity",
      },
    ];

    if (influencer?.tiktok_median_views) {
      stats.push({
        component: <ViewsIcon className="size-3.5 shrink-0" />,
        text: formatNumber(influencer?.tiktok_median_views),
        tooltipTextTranslation: "median_views",
        textParam: t("tiktok"),
      });
    }

    stats.push({
      component: <UsersIcon className="size-3.5 shrink-0" />,
      text: formatNumber(influencer?.tiktok_followers),
      tooltipTextTranslation: "follower_count",
    });

    return stats;
  };

  const openAmbassadorActionModal = (action: "accept" | "decline") => {
    if (isSentRequest) return;
    if ((ambassador.id ?? ambassador.ambassadorship_request_id) && influencer) {
      setAmbassadorActionModalOpen({
        action,
        agencyId,
        ambassador,
        companyId,
        entryPoint: "ambassador_queue",
        groupId,
        isOpen: true,
        pendingAmbassador: {
          ambassador_id: ambassador.id,
          ambassadorship_request_id: ambassador.ambassadorship_request_id,
          source: ambassador.source ?? "public_link",
        },
      });
    }
  };

  return (
    <div
      onClick={onSelect}
      className={clsxm(
        "border-soft-grey-300 flex h-full flex-col items-center justify-between gap-3 rounded-xl border bg-white p-3",
        !!onSelect && "cursor-pointer",
        isSelected && "border-primary-dark-solid border-2 shadow-md",
      )}>
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="border-neutral-grey-light bg-neutral-grey-light size-10 shrink-0 rounded border">
              <img src={influencer?.picture320!} className="object-cover" />
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-neutral-black truncate text-sm leading-3 font-semibold">
                {influencer?.user.first_name}
              </span>
              <InfluencerSocials influencer={influencer as TSidebarInfluencer} />
            </div>
          </div>
          {influencerVerificationCategory && (
            <InfluencerStatisticsRow
              className="grid shrink-0 grid-cols-2 gap-x-3 gap-y-2"
              stats={influencerVerificationCategory === "tiktok" ? getTiktokStats() : getInstagramStats()}
              statsContainerClassName="flex-row justify-end gap-1"
              textClassName="whitespace-nowrap"
            />
          )}
        </div>
        {!isSentRequest && ambassador.source === "public_link" && (
          <span className="bg-neutral-white-dark text-neutral-black text-2xs flex h-5 w-fit items-center rounded-full px-2 font-semibold">
            {t("referral:joined_with_your_code")}
          </span>
        )}
        {influencerVerificationCategory && (
          <div className="text-neutral-grey-dark text-2xs flex items-center justify-between gap-2 leading-2.5">
            {formattedDateVerified && <span className="shrink-0">{formattedDateVerified}</span>}
            <span className="ml-auto flex min-w-0 items-center gap-1">
              {influencerVerificationCategory === "tiktok" ? (
                <TiktokIcon className="h-auto w-2.5 shrink-0" />
              ) : (
                <InstagramIcon className="h-auto w-2.5 shrink-0" />
              )}
              <span className="truncate">
                {t("referral:social_kpis", {
                  social: t(influencerVerificationCategory === "tiktok" ? "tiktok" : "instagram"),
                })}
              </span>
            </span>
          </div>
        )}
        {!!ambassador.completed_campaigns?.length && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="bg-primary-light-solid text-primary-dark-solid text-2xs flex h-5 w-fit items-center rounded-full px-2 font-semibold">
              {t("referral:worked_with_you")}
            </span>
            {lastRatedCampaign?.rating && (
              <>
                <span
                  data-tip
                  data-tooltip-id={lastRatingTooltipId}
                  className="bg-neutral-white-dark text-neutral-black text-2xs flex h-5 w-fit items-center gap-1 rounded-full px-2 font-semibold">
                  <span className="pt-px">
                    {t("referral:you_rated_stars", {rating: +lastRatedCampaign.rating.toFixed(1)})}
                  </span>
                  <StarIcon className="text-warning-medium-yellow size-2.5 shrink-0" />
                </span>
                <Tooltip
                  id={lastRatingTooltipId}
                  delayShow={200}
                  place="bottom"
                  className="max-w-60 p-2"
                  variant="dark">
                  <span className="text-xs text-white">
                    {t("referral:last_rating_tooltip", {campaign: lastRatedCampaign.title})}
                  </span>
                </Tooltip>
              </>
            )}
            {!!ambassador.completed_campaigns?.length && (
              <div
                data-tip
                data-tooltip-id={campaignsTooltipId}
                className="shrink-0"
                onClick={e => e.stopPropagation()}>
                <Tooltip
                  id={campaignsTooltipId}
                  clickable
                  delayShow={200}
                  place="bottom"
                  className="max-w-75 p-2"
                  variant="dark">
                  <div className="scrollbar-thin-dark flex max-h-60 flex-col gap-0.5 overflow-y-auto">
                    {ambassador.completed_campaigns.map(campaign => (
                      <Link
                        key={campaign.id}
                        to="/campaign/$campaignId"
                        params={{campaignId: campaign.id}}
                        search={{name: influencerSearchName, tab: "reporting"}}
                        target="_blank"
                        className="flex items-center gap-2 rounded px-1.5 py-1 no-underline transition-colors hover:bg-white/10">
                        {campaign.picture ? (
                          <img src={campaign.picture} className="size-6 shrink-0 rounded-full object-cover" />
                        ) : (
                          <div className="bg-neutral-grey-light-dark size-6 shrink-0 rounded-full" />
                        )}
                        <span className="min-w-0 flex-1 truncate text-xs font-medium text-white">{campaign.title}</span>
                        {campaign.rating !== null && (
                          <span className="flex shrink-0 items-center gap-0.5 text-xs font-medium text-white">
                            <span className="pt-px">{+campaign.rating.toFixed(1)}</span>
                            <StarIcon className="text-warning-medium-yellow size-2.5" />
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </Tooltip>
                <OverlappingAvatars
                  avatarClassName="bg-neutral-grey-light-dark"
                  containerClassName="w-16 overflow-visible"
                  keyPrefix={campaignsTooltipId}
                  overlap={8}
                  overflowCountClassName="pl-2"
                  pictures={ambassador.completed_campaigns.map(campaign => campaign.picture)}
                  size={24}
                  totalCount={ambassador.completed_campaigns.length}
                />
              </div>
            )}
          </div>
        )}
        {!isSentRequest && !!ambassador.message && (
          <>
            <TruncatedTextWithMore
              className="text-neutral-grey-dark items-start text-xs leading-4"
              icon={<MessageIcon className="mt-1 size-2.5 shrink-0" />}
              moreClassName="text-neutral-black"
              moreProps={onSelect ? undefined : {"data-tooltip-id": messageTooltipId}}
              onClick={
                onSelect
                  ? () => {
                      onSelect();
                      scrollToInfluencerSidebarMessage();
                    }
                  : undefined
              }>
              {ambassador.message}
            </TruncatedTextWithMore>
            {!onSelect && (
              <Tooltip
                id={messageTooltipId}
                clickable
                delayShow={200}
                place="bottom"
                className="max-w-75 p-2"
                variant="dark">
                <div className="scrollbar-thin-dark max-h-60 overflow-y-auto text-xs whitespace-pre-line text-white">
                  {ambassador.message}
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>
      {footer}
      {showActions && !isSentRequest && (
        <Can I="update" a="AmbassadorApplication">
          <div className="flex w-full gap-3">
            <Button
              buttonStyle="White"
              className="size-12 shrink-0"
              onClick={() => openAmbassadorActionModal("decline")}>
              <TimesIcon className="text-danger-medium-red size-3.5" />
            </Button>
            <Button
              buttonStyle="White"
              className="h-12 w-full px-8"
              onClick={() => openAmbassadorActionModal("accept")}>
              <span className="font-medium">{t("accept")}</span>
            </Button>
          </div>
        </Can>
      )}
    </div>
  );
};

export default AmbassadorApplication;
